// src/pages/Cabinet.jsx
// Кабинет Наблюдателя — «пульт управления сознанием».
// Без левой колонки. Всё — в одном ScrollView.

import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Avatar,
  alpha,
} from '@mui/material';
import {
  Person as PersonIcon,
  Chat as ChatIcon,
  Psychology as PsychologyIcon,
  Lightbulb as LightbulbIcon,
  Flag as FlagIcon,
  ListAlt as ListAltIcon,
  Public as PublicIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import { useGameSession } from '../components/game_client/GameSessionContext';
import CharacterCard from '../components/character_card/CharacterCard';
import SemanticChat from '../components/game_client/SemanticChat';
import UserAvatar from '../components/common/UserAvatar';
import {
  addChatMessage,
} from '../store/slices/gameSlice';

// ============================================================================
// Вспомогательные компоненты
// ============================================================================

const CompactEventList = ({ events }) => {
  const list = Array.isArray(events) ? events : [];

  if (list.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <ChatIcon sx={{ fontSize: 32, color: 'text.secondary', opacity: 0.2, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">Событий нет</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {[...list].reverse().map((event) => (
        <Box
          key={event.id}
          sx={{
            p: 1,
            mb: 0.5,
            borderRadius: 1,
            borderLeft: 3,
            borderColor:
              event.type === 'error'
                ? 'error.main'
                : event.type === 'success'
                  ? 'success.main'
                  : 'primary.main',
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.2),
          }}
        >
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
            {event.data?.message || event.data?.text}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.5rem' }}>
            {event.timestamp || event.data?.timestamp}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const IntentsList = ({ intents }) => {
  const list = Array.isArray(intents) ? intents : [];

  if (list.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <LightbulbIcon sx={{ fontSize: 32, color: 'text.secondary', opacity: 0.2, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">Нет активных намерений</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={0.5}>
      {list.map((intent, idx) => {
        const type = intent.intent || intent.type || intent.intent_type || '—';
        const spec = intent.spec || intent.params || {};
        const name =
          spec.name || spec.title || spec.description || intent.name || intent.title || 'Намерение';
        const priority = spec.priority || intent.priority || 'medium';

        return (
          <Paper
            key={idx}
            sx={{
              p: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: (theme) => alpha(theme.palette.background.default, 0.2),
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', fontSize: '0.7rem' }}>
              {String(name)}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
              <Chip label={String(type).replace(/_/g, ' ')} size="small" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.45rem' } }} />
              <Chip label={String(priority)} size="small" variant="outlined" color="warning" sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.45rem' } }} />
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
};

const SimpleGoalList = ({ goals }) => {
  const list = Array.isArray(goals) ? goals : [];

  if (list.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <FlagIcon sx={{ fontSize: 32, color: 'text.secondary', opacity: 0.2, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">Нет активных целей</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={0.5}>
      {list.map((g, idx) => {
        const name = g.title || g.name || g.description || `Цель #${idx + 1}`;
        const status = (g.status || 'active').toLowerCase();
        return (
          <Paper key={idx} sx={{ p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
              {String(name).substring(0, 120)}
            </Typography>
            <Chip label={status} size="small" variant="outlined" sx={{ height: 16, mt: 0.5, '& .MuiChip-label': { fontSize: '0.45rem' } }} />
          </Paper>
        );
      })}
    </Stack>
  );
};

const SimpleTaskList = ({ tasks }) => {
  const list = Array.isArray(tasks) ? tasks : [];

  if (list.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <ListAltIcon sx={{ fontSize: 32, color: 'text.secondary', opacity: 0.2, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">Нет активных задач</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={0.5}>
      {list.map((t, idx) => {
        const name = t.name || t.title || t.description || `Задача #${idx + 1}`;
        const status = (t.status || 'active').toLowerCase();
        return (
          <Paper key={idx} sx={{ p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
              {String(name).substring(0, 120)}
            </Typography>
            <Chip label={status} size="small" variant="outlined" color="info" sx={{ height: 16, mt: 0.5, '& .MuiChip-label': { fontSize: '0.45rem' } }} />
          </Paper>
        );
      })}
    </Stack>
  );
};

// ============================================================================
// Главный компонент
// ============================================================================

const TABS = {
  EVENTS: 0,
  CONSCIOUSNESS: 1,
  INTENTS: 2,
  GOALS: 3,
  TASKS: 4,
};

export default function Cabinet() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const chatMessages = useSelector((state) => state.game.chatMessages || []);
  const access = useSelector((state) => state.auth?.access);

  const {
    isConnected,
    isConnecting,
    isInitializing,
    actionInProgress,
    characterState,
    emotions,
    goals,
    tasks,
    intents,
    events,
    selectedCharacter,
    selectedCharacterId,
    availableCharacters,
    charactersLoading,
    locationInfo,
    consciousness,
    detailedTasks,
    connect,
    selectCharacter,
    refreshState,
    loadEmotions,
    loadGoals,
    loadTasks,
    loadIntents,
    loadConsciousness,
    loadDetailedTasks,
    sendDialogue,
    sendSuggestion,
    getThoughtStream,
    isThoughtsLoading,
  } = useGameSession();

  const [activeTab, setActiveTab] = useState(TABS.CONSCIOUSNESS);
  const [showSelector, setShowSelector] = useState(false);

  const handleSelectCharacter = useCallback(
    async (character) => {
      if (!character?.id) return false;
      const result = await selectCharacter(character, true);
      if (result) setShowSelector(false);
      return result;
    },
    [selectCharacter]
  );

  const handleRefresh = useCallback(async () => {
    if (!selectedCharacterId) return;
    try {
      await refreshState();
      await Promise.all([
        loadEmotions(selectedCharacterId, true),
        loadGoals(selectedCharacterId, true),
        loadTasks(selectedCharacterId, true),
        loadIntents(selectedCharacterId, true),
        loadConsciousness(selectedCharacterId, true),
        loadDetailedTasks(selectedCharacterId, true),
      ]);
    } catch (e) {
      console.warn('Refresh failed:', e);
    }
  }, [
    selectedCharacterId,
    refreshState,
    loadEmotions,
    loadGoals,
    loadTasks,
    loadIntents,
    loadConsciousness,
    loadDetailedTasks,
  ]);

  const safeIntents = Array.isArray(intents) ? intents : [];
  const safeGoals = Array.isArray(goals) ? goals : [];
  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const safeEvents = Array.isArray(events) ? events : [];
  const safeDetailed = Array.isArray(detailedTasks) ? detailedTasks : [];

  if (!selectedCharacter) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PersonIcon sx={{ fontSize: 60, opacity: 0.3, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Персонаж не выбран</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Выберите персонажа для входа в Кабинет
          </Typography>
          <Button variant="contained" onClick={() => setShowSelector(true)}>
            Выбрать персонажа
          </Button>
        </Paper>

        <CharacterSelectorDialog
          open={showSelector}
          onClose={() => setShowSelector(false)}
          characters={availableCharacters}
          loading={charactersLoading}
          onSelect={handleSelectCharacter}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Компактная карточка персонажа */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          m: 1.5,
          mb: 0,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.7),
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          {/* Игрок (аватар + имя игрока) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <UserAvatar
              access={access}
              size="md"
              tooltip={access?.display_name || access?.email || 'Игрок'}
            />
            <Box sx={{ minWidth: 0, maxWidth: 160 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.55rem', lineHeight: 1 }}>
                Игрок
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {access?.display_name || access?.email || '—'}
              </Typography>
            </Box>
          </Box>

          {/* Divider */}
          <Box
            sx={{
              width: '1px',
              height: 36,
              bgcolor: 'divider',
              display: { xs: 'none', sm: 'block' },
            }}
          />

          {/* Персонаж */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 200 }}>
            <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 700 }}>
              {selectedCharacter.name?.[0]?.toUpperCase() || '?'}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.55rem', lineHeight: 1 }}>
                Персонаж
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {selectedCharacter.name || 'Без имени'}
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                <Chip label={`Ур. ${characterState?.level || selectedCharacter.level || 1}`} size="small" sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.55rem' } }} />
                <Chip label={characterState?.race || selectedCharacter.race || '—'} size="small" variant="outlined" sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.55rem' } }} />
                {locationInfo?.name && (
                  <Chip icon={<PublicIcon />} label={locationInfo.name} size="small" color="info" variant="outlined" sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.55rem' } }} />
                )}
                {!isConnected && (
                  <Chip label="Офлайн" size="small" color="error" variant="outlined" sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.55rem' } }} />
                )}
                {isConnected && (
                  <Chip label="В сети" size="small" color="success" variant="outlined" sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.55rem' } }} />
                )}
              </Stack>
            </Box>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => setShowSelector(true)} sx={{ fontSize: '0.7rem' }}>
              Сменить
            </Button>
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={!isConnected} sx={{ fontSize: '0.7rem' }}>
              Обновить
            </Button>
            <Button
              size="small"
              variant="contained"
              color="warning"
              startIcon={<PublicIcon />}
              onClick={() => navigate('/game/world')}
              disabled={!isConnected}
              sx={{ fontSize: '0.7rem' }}
            >
              В МИР →
            </Button>
          </Stack>
        </Stack>

        {!isConnected && !isConnecting && (
          <Box sx={{ mt: 1 }}>
            <Button variant="contained" color="success" size="small" onClick={() => connect(selectedCharacterId)} sx={{ fontSize: '0.7rem' }}>
              Подключиться
            </Button>
          </Box>
        )}
      </Paper>

      {/* Табы */}
      <Box sx={{ mt: 1.5, mx: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 36 }}
        >
          <Tab icon={<ChatIcon sx={{ fontSize: 16 }} />} label="События" sx={{ minHeight: 36, textTransform: 'none', fontSize: '0.7rem', py: 0.5 }} />
          <Tab icon={<PsychologyIcon sx={{ fontSize: 16 }} />} label="Сознание" sx={{ minHeight: 36, textTransform: 'none', fontSize: '0.7rem', py: 0.5 }} />
          <Tab icon={<LightbulbIcon sx={{ fontSize: 16 }} />} label="Намерения" sx={{ minHeight: 36, textTransform: 'none', fontSize: '0.7rem', py: 0.5 }} />
          <Tab icon={<FlagIcon sx={{ fontSize: 16 }} />} label="Цели" sx={{ minHeight: 36, textTransform: 'none', fontSize: '0.7rem', py: 0.5 }} />
          <Tab icon={<ListAltIcon sx={{ fontSize: 16 }} />} label="Задачи" sx={{ minHeight: 36, textTransform: 'none', fontSize: '0.7rem', py: 0.5 }} />
        </Tabs>
      </Box>

      {/* Содержимое */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, minHeight: 0 }}>
        {activeTab === TABS.EVENTS && (
          <Paper sx={{ p: 1.5 }}>
            <CompactEventList events={safeEvents} />
          </Paper>
        )}

        {activeTab === TABS.CONSCIOUSNESS && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {isInitializing || actionInProgress ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <SemanticChat
                emotions={emotions || {}}
                goals={safeGoals}
                intents={safeIntents}
                tasks={safeTasks}
                messages={chatMessages}
                dispatch={dispatch}
                isConnected={isConnected}
                characterId={selectedCharacterId}
                characterName={selectedCharacter?.name}
                questions={[]}
                onAnswerQuestion={null}
                sendDialogue={sendDialogue}
                sendSuggestion={sendSuggestion}
                getThoughtStream={getThoughtStream}
                thoughtStream={consciousness?.recent_reflections || []}
                isThoughtsLoading={isThoughtsLoading}
                loadDetailedTasks={loadDetailedTasks}
                loadEmotions={loadEmotions}
                loadGoals={loadGoals}
                loadTasks={loadTasks}
                loadIntents={loadIntents}
                loadConsciousness={loadConsciousness}
                refreshState={refreshState}
                onSendMessage={async (msg) => {
                  if (!selectedCharacterId || !sendDialogue) return;
                  try {
                    const response = await sendDialogue(selectedCharacterId, msg.text);
                    if (response?.response) {
                      dispatch(
                        addChatMessage({
                          sender: 'ai',
                          text: response.response.text || '...',
                          time: new Date().toLocaleTimeString(),
                        })
                      );
                    }
                    setTimeout(() => {
                      refreshState();
                      loadEmotions(selectedCharacterId);
                      loadGoals(selectedCharacterId);
                      loadTasks(selectedCharacterId);
                      loadIntents(selectedCharacterId);
                      loadConsciousness(selectedCharacterId);
                      loadDetailedTasks(selectedCharacterId, true).catch(() => {});
                      if (getThoughtStream) getThoughtStream(selectedCharacterId);
                    }, 1500);
                  } catch (error) {
                    dispatch(
                      addChatMessage({
                        sender: 'system',
                        text: `❌ Ошибка: ${error.message || 'Неизвестная ошибка'}`,
                        time: new Date().toLocaleTimeString(),
                      })
                    );
                  }
                }}
              />
            )}
          </Box>
        )}

        {activeTab === TABS.INTENTS && (
          <Paper sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, fontSize: '0.7rem' }}>
              Активные намерения ({safeIntents.length})
            </Typography>
            <IntentsList intents={safeIntents} />
          </Paper>
        )}

        {activeTab === TABS.GOALS && (
          <Paper sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, fontSize: '0.7rem' }}>
              Активные цели ({safeGoals.length})
            </Typography>
            <SimpleGoalList goals={safeGoals} />
          </Paper>
        )}

        {activeTab === TABS.TASKS && (
          <Paper sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, fontSize: '0.7rem' }}>
              Активные задачи ({safeTasks.length})
            </Typography>
            <SimpleTaskList tasks={safeTasks.length > 0 ? safeTasks : safeDetailed} />
          </Paper>
        )}
      </Box>

      <CharacterSelectorDialog
        open={showSelector}
        onClose={() => setShowSelector(false)}
        characters={availableCharacters}
        loading={charactersLoading}
        onSelect={handleSelectCharacter}
      />
    </Box>
  );
}

// ============================================================================
// Диалог выбора персонажа
// ============================================================================

function CharacterSelectorDialog({ open, onClose, characters, loading, onSelect }) {
  return (
    <Box
      sx={{
        display: open ? 'flex' : 'none',
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(0,0,0,0.5)',
        zIndex: 1300,
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
      onClick={onClose}
    >
      <Paper
        sx={{ p: 2, maxWidth: 900, width: '100%', maxHeight: '80vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>Выбор персонажа</Typography>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : !Array.isArray(characters) || characters.length === 0 ? (
          <Alert severity="info">Нет доступных персонажей</Alert>
        ) : (
          <Grid container spacing={2}>
            {characters.map((char, idx) => (
              <Grid item xs={12} md={6} key={char.id || idx}>
                <CharacterCard
                  character={char}
                  isSelected={false}
                  onSelect={() => onSelect(char)}
                  showActivation={false}
                  index={idx}
                />
              </Grid>
            ))}
          </Grid>
        )}
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button onClick={onClose}>Закрыть</Button>
        </Box>
      </Paper>
    </Box>
  );
}
